import { useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import { Plus, Trash2, Settings, Link, Send, Bell, ExternalLink, Save, RefreshCw, Users, CheckCircle, XCircle, UserPlus } from 'lucide-react'
import { api } from '@/api/client'
import { useAuth } from '@/api/hooks/useAuth'
import { useSaveKeepAlive, useDeleteToken } from '@/api/hooks/useAccounts'
import { useLanguage } from '@/i18n/LanguageContext'
import type { Account, PersistentMenuItem } from '@/types'
import { useQueryClient } from '@tanstack/react-query'

interface Props {
  account: Account
  onSaved?: () => void
}

interface MenuForm {
  items: PersistentMenuItem[]
}

export function MenuOTN({ account, onSaved }: Props) {
  const { t } = useLanguage()
  const { data: user } = useAuth()
  const authToken = typeof window !== 'undefined' ? localStorage.getItem('auth_token') || '' : ''
  const returnTo = typeof window !== 'undefined' ? window.location.pathname : '/painel'
  const authUrl = `/auth?token=${encodeURIComponent(authToken)}&returnTo=${encodeURIComponent(returnTo)}`
  const qc = useQueryClient()
  const [saving, setSaving] = useState(false)
  const [sendingOTN, setSendingOTN] = useState(false)
  const [otnMessage, setOtnMessage] = useState('')
  const [otnStats, setOtnStats] = useState<{ total: number; available: number } | null>(null)
  const [loadingStats, setLoadingStats] = useState(false)
  const deleteToken = useDeleteToken()

  // Keep-Alive state
  const [kaEnabled, setKaEnabled] = useState(account.keepAliveEnabled || false)
  const [kaMessage, setKaMessage] = useState(account.keepAliveMessage || 'Ei, posso te mandar algo que vai te interessar?')
  const saveKeepAlive = useSaveKeepAlive()

  const { register, control, handleSubmit } = useForm<MenuForm>({
    defaultValues: {
      items: account.persistentMenu?.length
        ? account.persistentMenu
        : [{ title: '', url: '' }],
    },
  })

  const { fields, append, remove } = useFieldArray({ control, name: 'items' })

  const onSaveMenu = async (data: MenuForm) => {
    setSaving(true)
    try {
      await api.put(`/accounts/${account.igUserId}/persistent-menu`, { items: data.items })
      toast.success(t('Menu persistente salvo!'))
      qc.invalidateQueries({ queryKey: ['accounts'] })
      onSaved?.()
    } catch {
      toast.error(t('Erro ao salvar menu'))
    } finally {
      setSaving(false)
    }
  }

  const loadOTNStats = async () => {
    setLoadingStats(true)
    try {
      const stats = await api.get<{ total: number; available: number }>(`/accounts/${account.igUserId}/otn-stats`)
      setOtnStats(stats)
    } catch {
      toast.error(t('Erro ao carregar stats OTN'))
    } finally {
      setLoadingStats(false)
    }
  }

  const sendOTN = async () => {
    if (!otnMessage.trim()) {
      toast.error(t('Digite a mensagem OTN'))
      return
    }
    setSendingOTN(true)
    try {
      const result = await api.post<{ sent: number; failed: number }>(`/accounts/${account.igUserId}/send-otn`, { message: otnMessage })
      toast.success(`OTN ${t('enviado')}! ${result.sent} ${t('enviados')}, ${result.failed} ${t('falhas')}`)
      setOtnMessage('')
      loadOTNStats()
    } catch {
      toast.error(t('Erro ao enviar OTN'))
    } finally {
      setSendingOTN(false)
    }
  }

  const handleSaveKeepAlive = () => {
    saveKeepAlive.mutate(
      { igUserId: account.igUserId, keepAliveEnabled: kaEnabled, keepAliveMessage: kaMessage },
      {
        onSuccess: () => toast.success(t('Keep-Alive salvo!')),
        onError: () => toast.error(t('Erro ao salvar Keep-Alive')),
      },
    )
  }

  const tokens = account.tokens || []
  const validCount = tokens.filter(tk => tk.valid).length

  const handleDeleteToken = (index: number, profileName: string) => {
    if (!confirm(`${t('Remover o token do perfil')} "${profileName}"?`)) return
    deleteToken.mutate(
      { igUserId: account.igUserId, index },
      {
        onSuccess: () => toast.success(`${t('Token de')} "${profileName}" ${t('removido')}`),
        onError: () => toast.error(t('Erro ao remover token')),
      },
    )
  }

  return (
    <div className="space-y-6">
      {/* Perfis Vinculados (Multi-Token) */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`rounded-lg p-2 ${
              validCount >= 2 ? 'bg-emerald-500/10' : validCount === 1 ? 'bg-yellow-500/10' : 'bg-destructive/10'
            }`}>
              <Users className={`h-4 w-4 ${
                validCount >= 2 ? 'text-emerald-600 dark:text-emerald-400' : validCount === 1 ? 'text-yellow-600 dark:text-yellow-500' : 'text-destructive'
              }`} />
            </div>
            <div>
              <div className="text-sm font-semibold">{t('Perfis Vinculados')}</div>
              <div className="text-[10px] text-muted-foreground">
                {tokens.length === 0
                  ? t('Nenhum perfil vinculado')
                  : `${validCount} ${t('de')} ${tokens.length} ${t('perfil(is) ativo(s)')}`}
              </div>
            </div>
          </div>
          <Badge
            variant="outline"
            className={`text-[10px] ${
              validCount >= 2
                ? 'border-emerald-500/50 text-emerald-600 dark:text-emerald-400'
                : validCount === 1
                  ? 'border-yellow-500/50 text-yellow-600 dark:text-yellow-500'
                  : 'border-destructive/50 text-destructive'
            }`}
          >
            {validCount >= 2 ? t('Redundante') : validCount === 1 ? t('Sem redundancia') : t('Sem token')}
          </Badge>
        </div>

        {tokens.length > 0 && (
          <div className="space-y-2">
            {tokens.map((tk, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg border border-border bg-background/50 px-3 py-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {tk.valid ? (
                    <CheckCircle className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                  )}
                  <div className="min-w-0">
                    <span className="text-xs font-medium truncate block">{tk.profileName}</span>
                    <span className="text-[9px] text-muted-foreground">
                      {t('Adicionado em')} {new Date(tk.addedAt).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>
                <Badge
                  variant={tk.valid ? 'default' : 'destructive'}
                  className={`text-[9px] shrink-0 ${tk.valid ? 'bg-emerald-600' : ''}`}
                >
                  {tk.valid ? t('Valido') : t('Invalido')}
                </Badge>
                {tokens.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => handleDeleteToken(i, tk.profileName)}
                    disabled={deleteToken.isPending}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3 w-full gap-1.5 border-dashed text-xs"
          asChild
        >
          <a href={authUrl}>
            <UserPlus className="h-3 w-3" />
            {t('Adicionar perfil')}
          </a>
        </Button>

        {validCount < 2 && tokens.length > 0 && (
          <p className="mt-2 text-[10px] text-yellow-600 dark:text-yellow-500">
            {t('Recomendamos vincular pelo menos 2 perfis para redundancia. Se um perfil cair, o sistema usa automaticamente o outro.')}
          </p>
        )}
      </div>

      {/* Keep-Alive */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`rounded-lg p-2 ${kaEnabled ? 'bg-emerald-500/10' : 'bg-muted'}`}>
              <RefreshCw className={`h-4 w-4 ${kaEnabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`} />
            </div>
            <div>
              <div className="text-sm font-semibold">{t('Keep-Alive')}</div>
              <div className="text-[10px] text-muted-foreground">
                {t('Envia mensagem automatica antes da janela de 24h expirar')}
              </div>
            </div>
          </div>
          <Switch checked={kaEnabled} onCheckedChange={setKaEnabled} />
        </div>

        {kaEnabled && (
          <div className="mt-4 space-y-3">
            <div>
              <Label className="mb-1.5 text-xs text-muted-foreground">{t('Mensagem de keep-alive')}</Label>
              <Textarea
                value={kaMessage}
                onChange={(e) => setKaMessage(e.target.value)}
                placeholder={t('Oi! Tudo bem? Me responde aqui rapidinho...')}
                className="min-h-[80px] resize-none text-sm"
              />
              <p className="mt-1.5 text-[10px] text-muted-foreground">
                {t('Enviada automaticamente quando o contato esta entre 22-23h sem interagir. Se ele responder, a janela reseta por +24h.')}
              </p>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="text-[10px] font-medium text-muted-foreground mb-1">{t('Como funciona')}:</div>
              <ul className="text-[10px] text-muted-foreground space-y-0.5 list-disc list-inside">
                <li>{t('A cada 10 min, verifica contatos FB prestes a perder a janela (22-23h)')}</li>
                <li>{t('Envia a mensagem com')} <strong className="text-foreground">Quick Replies</strong> ({t('botoes clicaveis')})</li>
                <li>{t('O contato so precisa')} <strong className="text-foreground">{t('tocar num botao')}</strong> {t('pra resetar a janela +24h')}</li>
                <li>{t('Maximo 1 keep-alive por contato a cada 24h')}</li>
                <li>{t('Respeita o toggle de auto-reply por conversa')}</li>
              </ul>
              <div className="mt-2 flex gap-2">
                <span className="rounded-full bg-primary/10 border border-primary/20 px-2.5 py-1 text-[10px] font-medium text-primary">👍 Sim, manda!</span>
                <span className="rounded-full bg-muted border border-border px-2.5 py-1 text-[10px] font-medium text-muted-foreground">⏰ Agora não</span>
              </div>
            </div>

            <Button
              type="button"
              onClick={handleSaveKeepAlive}
              disabled={saveKeepAlive.isPending}
              className="w-full gap-2 text-sm"
            >
              <Save className="h-4 w-4" />
              {saveKeepAlive.isPending ? t('Salvando...') : t('Salvar Keep-Alive')}
            </Button>
          </div>
        )}
      </div>

      {/* Persistent Menu */}
      <form onSubmit={handleSubmit(onSaveMenu)}>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-muted p-2">
                <Settings className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <div className="text-sm font-semibold">{t('Menu Persistente')}</div>
                <div className="text-[10px] text-muted-foreground">
                  {t('Botoes fixos que aparecem no Messenger (max 3)')}
                </div>
              </div>
            </div>
            <Badge variant="outline" className="text-[10px]">
              {fields.length}/3
            </Badge>
          </div>

          <div className="space-y-2">
            {fields.map((field, index) => (
              <div key={field.id} className="flex items-center gap-2">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-xs font-bold text-muted-foreground">
                  {index + 1}
                </div>
                <div className="grid flex-1 grid-cols-2 gap-2">
                  <Input
                    placeholder={t('Titulo do botao')}
                    className="h-8 text-xs"
                    {...register(`items.${index}.title`)}
                  />
                  <div className="relative">
                    <Link className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="https://..."
                      className="h-8 pl-7 text-xs"
                      {...register(`items.${index}.url`)}
                    />
                  </div>
                </div>
                {fields.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => remove(index)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {fields.length < 3 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3 w-full border-dashed text-xs"
              onClick={() => append({ title: '', url: '' })}
            >
              <Plus className="mr-1.5 h-3 w-3" /> {t('Adicionar item')}
            </Button>
          )}

          <Button type="submit" disabled={saving} className="mt-4 w-full gap-2 text-sm">
            <Save className="h-4 w-4" />
            {saving ? t('Salvando...') : t('Salvar Menu')}
          </Button>
        </div>
      </form>

      {/* OTN Dispatch */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-purple-400/10 p-2">
              <Bell className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <div className="text-sm font-semibold">{t('Disparar OTN')}</div>
              <div className="text-[10px] text-muted-foreground">
                {t('Envie mensagem unica para quem autorizou')}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-[10px]"
            onClick={loadOTNStats}
            disabled={loadingStats}
          >
            <ExternalLink className="h-3 w-3" />
            {loadingStats ? t('Carregando...') : t('Ver Stats')}
          </Button>
        </div>

        {otnStats && (
          <div className="mb-4 grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-border bg-background/50 p-3 text-center">
              <div className="text-lg font-bold text-purple-600 dark:text-purple-400">{otnStats.total}</div>
              <div className="text-[10px] text-muted-foreground">{t('Total tokens')}</div>
            </div>
            <div className="rounded-lg border border-border bg-background/50 p-3 text-center">
              <div className="text-lg font-bold text-primary">{otnStats.available}</div>
              <div className="text-[10px] text-muted-foreground">{t('Disponiveis')}</div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label className="mb-1.5 text-xs text-muted-foreground">{t('Mensagem OTN')}</Label>
            <Textarea
              value={otnMessage}
              onChange={(e) => setOtnMessage(e.target.value)}
              placeholder={t('Digite a mensagem que sera enviada...')}
              className="min-h-[80px] resize-none text-sm"
            />
          </div>

          <Button
            type="button"
            onClick={sendOTN}
            disabled={sendingOTN || !otnMessage.trim()}
            className="w-full gap-2"
          >
            <Send className="h-4 w-4" />
            {sendingOTN ? t('Enviando...') : t('Disparar OTN')}
          </Button>
        </div>
      </div>
    </div>
  )
}
